<template>
  <s-page
    class="font-degular font-normal max-w-2xl"
    v-if="isDataLoaded && isStaff"
  >
    <header class="mb-0 mt-10 w-full">
      <h1 class="w-4/5 title-2 whitespace-normal text-left text-6xl">
        <div class="w-full">
          <img
            src="@/assets/svg/SLS-PrimaryHoriz-Fire.svg"
            alt="SEOULSPICE Logo Classic"
            class="mx-auto w-full h-full pt-5"
          />
        </div>

        <section class="my-4 flex flex-col">
          <div
            v-if="name"
            @click="openStoreSelector"
            class="flex flex-row items-center title-2 text-3xl leading-none overflow-visible cursor-pointer"
          >
            <unicon name="store" class="flex-shrink-0 w-6 h-6 text-gray-800" />
            <div class="ml-2 leading-none">{{ name }}</div>
            <unicon
              v-if="isManager && stores.length > 1"
              name="angle-down"
              class="flex-shrink-0 ml-1 w-8 h-8"
            />
          </div>
          <div
            v-if="address"
            class="mt-2 flex flex-row items-center title-2 text-lg sm:text-xl whitespace-normal"
          >
            <unicon
              name="location-point"
              class="flex-shrink-0 w-6 h-6 text-gray-800"
            />
            <div class="ml-2">{{ address }}</div>
          </div>
        </section>
      </h1>
    </header>

    <section class="mt-6">
      <button-list
        key="store"
        name="Store Settings"
        :searchable="false"
        :draggable="false"
        :open="false"
        :list="[
          'fees',
          'productCategories',
          'productLocations',
          'separator-notifications',
          'tasks',
          'ordering',
          'priceChange',
          'separator-hours',
          ...weekdays
        ]"
        :display="{
          fees: { name: 'Default Fee Values', active: true },
          productCategories: {
            name: 'Product Expense Categories',
            active: true
          },
          productLocations: {
            name: 'Product Locations',
            active: true
          },
          'separator-notifications': {
            name: 'Notifications',
            isSeparator: true
          },
          tasks: {
            name: 'Tasks',
            active: notifications.tasks.active
          },
          ordering: {
            name: 'Orders',
            active: notifications.ordering.active
          },
          priceChange: {
            name: 'Price Change',
            active: notifications.priceChange.active
          },
          'separator-hours': {
            name: 'Shift Hours',
            isSeparator: true
          },
          ...Object.fromEntries(
            weekdays.map((day) => [
              day,
              { name: capitalize(day), active: true }
            ])
          )
        }"
        display-property="name"
        is-active-property="active"
        @edit="handleStoreMenu"
      />

      <button-list
        v-if="isAdmin"
        key="users"
        :name="usersListName"
        :searchable="true"
        button="Create New"
        :button-data="null"
        :draggable="false"
        :open="false"
        :list="allUsersDisplayMap"
        :display="allUsersDisplayMap"
        display-property="name"
        is-active-property="active"
        :index-color="'none'"
        :sort-options="userSortOptions"
        :no-capitalize="true"
        primary-label="Email"
        :default-badge-index="1"
        @edit="$modal.show('modal--edit-user', $event)"
      />

      <button-list
        v-if="vendors"
        key="vendors"
        name="Vendors"
        :button="isAdmin ? 'Create New' : null"
        :draggable="false"
        :open="false"
        :list="vendors"
        :display="vendors"
        display-property="name"
        is-active-property="active"
        @edit="$modal.show('modal--edit-vendor', $event)"
      />

      <button-list
        v-if="dailyOrdering"
        key="orderingSequence"
        name="Products (In Count Sequence)"
        :button="isAdmin ? 'Create New' : null"
        :button-data="null"
        :draggable="isAdmin"
        :open="false"
        :list="dailyOrdering"
        :display="products"
        display-property="name"
        is-active-property="active"
        :show-index="false"
        :sort-alpha="false"
        :index-color="'none'"
        :right-badge="true"
        :right-badge-callback="(item) => productLocation(item.id)"
        @edit="$modal.show('modal--edit-product', $event)"
        @update="updateOrderingSequence($event)"
      />

      <button-list-recipes @edit="$modal.show('modal--edit-recipe', $event)" />

      <button-list
        v-for="(name, index) in shifts"
        :key="`tasks_${index}`"
        :name="`Tasks (${capitalize(name)})`"
        :button="isAdmin ? 'Create New' : null"
        :button-data="{ shift: index }"
        :draggable="isAdmin"
        :open="false"
        :list="tasksByShift(index)"
        :display="tasks"
        :icons="['bell']"
        icon-property="notification"
        display-property="name"
        second-column="frequency"
        @edit="$modal.show('modal--edit-task', $event)"
        @update="updateTaskList({ shift: index, list: $event })"
      />
    </section>

    <modal-edit-hours />
    <modal-edit-task />
    <modal-edit-product />
    <modal-edit-product-categories />
    <modal-edit-locations />
    <modal-edit-vendor />
    <modal-edit-user />
    <modal-switch-stores />
    <modal-edit-notifications />
    <modal-edit-fees />
  </s-page>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import ModalEditTask from '@/components/ModalEditTask'
import ModalEditProduct from '@/components/ModalEditProduct'
import ModalEditVendor from '@/components/ModalEditVendor'
import ModalEditHours from '@/components/ModalEditHours'
import ModalSwitchStores from '@/components/ModalSwitchStores'
import ModalEditNotifications from '@/components/ModalEditNotifications'
import ModalEditFees from '@/components/ModalEditFees'
import ModalEditProductCategories from '@/components/ModalEditProductCategories'
import ButtonList from '@/components/ButtonList'
import ButtonListRecipes from '@/components/ButtonListRecipes'
import SPage from '@/components/SPage'
import { weekdays } from '@/const.js'
import { capitalize, format } from '@/utils.js'
import ModalEditLocations from '@/components/ModalEditLocations'
import ModalEditUser from '@/components/ModalEditUser'

export default {
  name: 'SettingsView',
  data() {
    return {
      weekdays,
      loadingDots: 1,
      loadingTimer: null
    }
  },
  components: {
    ModalEditHours,
    ModalEditTask,
    ModalEditProduct,
    ModalEditProductCategories,
    ModalEditVendor,
    ModalSwitchStores,
    ModalEditNotifications,
    ModalEditFees,
    ButtonList,
    ButtonListRecipes,
    SPage,
    ModalEditLocations,
    ModalEditUser
  },
  computed: {
    ...mapGetters('global', ['settingsStoreIds']),
    ...mapGetters('storeModule', [
      'shifts',
      'dailyOrdering',
      'name',
      'address',
      'tasksByShift',
      'notifications',
      'recipeUnitCostById',
      'productPriceByProductId',
      'localProductIdFromGenericId'
    ]),
    ...mapGetters('storeModule/tasks', ['tasks']),
    ...mapGetters('storeModule/vendors', ['vendors']),
    ...mapGetters('recipes', ['recipes']),
    ...mapGetters('storeModule/products', ['products']),

    ...mapGetters('user', [
      'isDeveloper',
      'isAdmin',
      'isManager',
      'isStaff',
      'stores',
      'storeName'
    ]),
    ...mapGetters('admin', ['allUsers', 'isLoading']),
    ...mapGetters('config', ['isDataLoaded']),
    usersListName() {
      return this.isLoading ? 'Users' + '.'.repeat(this.loadingDots) : 'Users'
    },
    allUsersDisplayMap() {
      const map = {}
      this.allUsers
        .filter((user) => user.status !== 'hidden')
        .forEach((user) => {
          const primaryStoreName = this.settingsStoreIds[user.store]
            ? this.settingsStoreIds[user.store].name
            : user.store || ''
          const extraStores =
            user.stores && user.stores.length > 1 ? user.stores.length - 1 : 0
          const storeName =
            extraStores > 0
              ? `${primaryStoreName} +${extraStores}`
              : primaryStoreName
          const extraRoles =
            user.roles && user.roles.length > 1 ? user.roles.length - 1 : 0
          const roleName =
            extraRoles > 0
              ? `${user.role} +${extraRoles}`
              : user.role || ''
          map[user.id] = {
            ...user,
            name: user.email || user.id,
            userName:
              user.name || user.authDisplayName || user.email || user.id,
            storeName,
            roleName,
            active: !user.disabled
          }
        })
      return map
    },
    userSortOptions() {
      return [
        { label: 'Name', getValue: (user) => user.userName || '' },
        { label: 'Store', getValue: (user) => user.storeName || '' },
        { label: 'Role', getValue: (user) => user.roleName || '' }
      ]
    }
  },
  watch: {
    isLoading(val) {
      if (val) {
        this.loadingDots = 1
        this.loadingTimer = setInterval(() => {
          this.loadingDots = (this.loadingDots % 3) + 1
        }, 600)
      } else {
        clearInterval(this.loadingTimer)
        this.loadingTimer = null
      }
    }
  },
  mounted() {
    if (this.isAdmin) {
      this.$store.dispatch('admin/fetchAllUsers')
    }
  },
  beforeDestroy() {
    clearInterval(this.loadingTimer)
  },
  methods: {
    capitalize,
    format,
    ...mapActions('storeModule', ['updateTaskList', 'updateOrderingSequence']),
    handleStoreMenu(item) {
      if (item.id === 'fees') {
        this.$modal.show('modal--edit-fees')
      } else if (item.id === 'productCategories') {
        this.$modal.show('modal--edit-product-categories')
      } else if (item.id === 'productLocations') {
        this.$modal.show('modal--edit-locations')
      } else if (['tasks', 'ordering', 'priceChange'].includes(item.id)) {
        this.$modal.show('modal--edit-notifications', item)
      } else if (weekdays.includes(item.id)) {
        this.$modal.show('modal--edit-hours', item)
      }
    },
    log(e) {
      console.log(e)
    },
    productUnitCost(id) {
      const localId = this.localProductIdFromGenericId(id)
      const price = this.productPriceByProductId(localId)
      return price ? price.pricePerCountingUnit : 0
    },
    productLocation(id) {
      try {
        const product = this.products[id]
        if (!product) {
          return ''
        }
        return product.location ? product.location.toUpperCase() : ''
      } catch (error) {
        // console.error('Error getting product location for ID:', id, error)
        return ''
      }
    },
    getProductPrice(id) {
      try {
        // Since we're working with two different ID systems, we need to find the right
        // mapping. In this case, the ID might already be the local ID, not the genericId.

        // Try direct approach first - assume id is already the local ID
        let localId = id
        let price = this.productPriceByProductId(localId)

        // If price found, use it
        if (price && price.pricePerCountingUnit !== undefined) {
          return price.pricePerCountingUnit || 0
        }

        // If not found, get the local products data directly
        const productsData = this.$store.state.storeModule.products.data
        if (!productsData || !productsData.length) {
          return 0
        }

        // Find product with matching genericProductId
        const product = productsData.find((p) => p.genericProductId === id)
        if (!product) {
          return 0
        }

        // Use the product.id to get price
        localId = product.id
        price = this.productPriceByProductId(localId)

        // Return the price or 0
        return price && price.pricePerCountingUnit
          ? price.pricePerCountingUnit
          : 0
      } catch (error) {
        console.error('Error getting product price for ID:', id, error)
        return 0
      }
    },
    openStoreSelector() {
      if (this.isManager && this.stores.length > 1) {
        this.$modal.show('modal--select-store', {
          list: this.stores.slice().sort((a, b) => {
            const nameA = this.settingsStoreIds[a]
              ? this.settingsStoreIds[a].name
              : a
            const nameB = this.settingsStoreIds[b]
              ? this.settingsStoreIds[b].name
              : b
            return nameA.toLowerCase().localeCompare(nameB.toLowerCase())
          }),
          display: (e) =>
            e && this.settingsStoreIds[e] && this.settingsStoreIds[e].name
              ? this.settingsStoreIds[e].name
              : '',
          currentStore: this.storeName
        })
      }
    }
  }
}
</script>
